/**
 * OperatingCanvasFigure — public-safe SVG of the eight operating lenses.
 *
 * No private architecture, internal-method labels, or vendor mentions. Pure
 * capability-layer disclosure of the frame JL uses to think about businesses.
 */

const lenses = [
  { id: 'business', label: 'Business', sub: ['Who · What · Why', 'For whom'] },
  { id: 'revenue', label: 'Revenue', sub: ['Sources · Cadence', 'Concentration'] },
  { id: 'cost', label: 'Cost', sub: ['Fixed · Variable', 'Hidden · Opportunity'] },
  { id: 'process', label: 'Process', sub: ['Inputs → Steps', 'Decisions → Outputs'] },
  { id: 'data', label: 'Data', sub: ['Sources of truth', 'Flows · Drift · Owners'] },
  { id: 'risk', label: 'Risk', sub: ['Surfaces · Heat', 'Decay · Mitigations'] },
  { id: 'proof', label: 'Proof', sub: ['Evidence · Receipt', 'Acceptance'] },
  { id: 'delivery', label: 'Delivery', sub: ['Artifact · Audience', 'Cadence · Boundary'] },
];

export function OperatingCanvasFigure() {
  return (
    <figure className="overflow-hidden rounded-sm border border-accent/30 bg-white p-6 shadow-[0_18px_60px_rgba(11,13,16,0.06)]">
      <svg
        viewBox="0 0 720 360"
        role="img"
        aria-labelledby="operating-canvas-title operating-canvas-desc"
        className="h-auto w-full"
      >
        <title id="operating-canvas-title">The Operating Canvas — eight lenses</title>
        <desc id="operating-canvas-desc">
          A 2-by-4 grid of operating lenses: Business, Revenue, Cost, Process, Data,
          Risk, Proof, Delivery. Each lens covers one dimension of how a business
          actually runs.
        </desc>

        {/* Title strip */}
        <rect x="0" y="0" width="720" height="48" fill="#0b0d10" />
        <text
          x="360"
          y="30"
          textAnchor="middle"
          fontFamily="Inter, system-ui, sans-serif"
          fontSize="14"
          fontWeight="700"
          fill="#f6f3eb"
          letterSpacing="2"
        >
          THE OPERATING CANVAS
        </text>

        {/* Eight lens cells */}
        {lenses.map((lens, idx) => {
          const col = idx % 4;
          const row = Math.floor(idx / 4);
          const x = 24 + col * 168;
          const y = 72 + row * 130;
          return (
            <g key={lens.id}>
              <rect
                x={x}
                y={y}
                width="160"
                height="120"
                rx="4"
                fill="#fdfcf7"
                stroke="#0b0d10"
                strokeOpacity="0.12"
              />
              <rect x={x} y={y} width="160" height="6" fill="#b27d00" />
              <text
                x={x + 14}
                y={y + 38}
                fontFamily="Inter, system-ui, sans-serif"
                fontSize="16"
                fontWeight="800"
                fill="#0b0d10"
              >
                {lens.label}
              </text>
              <text
                x={x + 14}
                y={y + 62}
                fontFamily="Inter, system-ui, sans-serif"
                fontSize="11"
                fontWeight="500"
                fill="#0b0d10"
                opacity="0.65"
              >
                {lens.sub.map((line, lineIndex) => (
                  <tspan key={line} x={x + 14} dy={lineIndex === 0 ? 0 : 16}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-4 text-xs text-ink/55">
        Eight lenses, one business. Each is a question shape, not a checklist.
      </figcaption>
    </figure>
  );
}
